/**
 * syntaxLinter — Live syntax linter for the Code-node editor.
 *
 * sl-93dd.4: Surfaces real parse problems to the user instead of
 * waiting for the sandbox to throw. Two signal sources are combined:
 *
 * 1. Lezer error nodes (`type.isError`) — the language packs
 *    (`@codemirror/lang-javascript`, `@codemirror/lang-python`) ship
 *    full Lezer grammars, so incomplete expressions, unmatched brackets,
 *    and other recoverable parse errors surface as `⚠` tokens in the
 *    syntax tree. We emit a Diagnostic for each.
 *
 * 2. Python top-level indent (`checkPythonTopLevelIndent`) — Lezer's
 *    Python grammar does not enforce indentation rules (INDENT/DEDENT
 *    handling is runtime-only in CPython), so `  a = 1` parses cleanly
 *    at the tree level. We add a line-based scanner for the
 *    indent-error case because it is the single most common Python
 *    IndentationError source for pasted code.
 *
 * Both paths emit the same `Diagnostic` shape, so the CodeMirror
 * `linter()` extension treats them uniformly (gutter marker + inline
 * squiggle + hover tooltip).
 */

import { syntaxTree } from '@codemirror/language';
import type { Diagnostic } from '@codemirror/lint';
import { linter } from '@codemirror/lint';
import type { EditorState } from '@codemirror/state';

import { parseTemplateTokens } from './template-tokens';

export type SyntaxLinterLanguage = 'javascript' | 'python' | 'json';

/**
 * Template placeholders (`{{steps.X.Y}}`, `{{credential.…}}`, …) are
 * pre-rendered by the runner before the sandbox sees the code, so the
 * editor parser has no chance of understanding them. Any error range
 * that overlaps a template token is discarded to keep the gutter
 * free of spurious "syntax error" markers on legitimate templates.
 */
function rangeOverlapsTemplate(
  templates: readonly { from: number; to: number }[],
  from: number,
  to: number
): boolean {
  for (const t of templates) {
    if (from < t.to && t.from < to) return true;
  }
  return false;
}

/**
 * Collect Lezer error nodes and clamp their range so even end-of-input
 * zero-length error markers render a visible squiggle. Errors that
 * sit inside a template placeholder are suppressed.
 */
function collectLezerErrors(state: EditorState): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  const tree = syntaxTree(state);
  const docLength = state.doc.length;
  const templates = parseTemplateTokens(state.doc.toString());

  tree.cursor().iterate(node => {
    if (!node.type.isError) return;
    let from = node.from;
    let to = node.to;
    if (to <= from) {
      if (from > 0) from = from - 1;
      else to = Math.min(from + 1, docLength);
      if (to <= from) return;
    }
    if (rangeOverlapsTemplate(templates, from, to)) return;
    diagnostics.push({
      from,
      to,
      severity: 'error',
      source: 'syntax',
      message: 'Syntax error',
    });
  });

  return diagnostics;
}

/**
 * Flag Python lines that start with whitespace but are not inside a
 * block (the previous non-empty, non-comment line does not end with
 * `:`). Lezer does not catch this because Python's indent rules are
 * enforced at tokenization time, not grammar time — a browser-side
 * Lezer tree parses `  a = 1` as a valid AssignStatement.
 */
function checkPythonTopLevelIndent(state: EditorState): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  const doc = state.doc;
  const lines: { from: number; to: number; text: string }[] = [];
  for (let i = 1; i <= doc.lines; i++) {
    const line = doc.line(i);
    lines.push({ from: line.from, to: line.to, text: line.text });
  }

  for (let i = 0; i < lines.length; i++) {
    const { from, to, text } = lines[i];
    const leading = text.match(/^[ \t]+/)?.[0];
    if (!leading) continue;
    if (leading.length === text.length) continue; // whitespace-only line
    // Skip comments — shebang-style top-level comments are tolerated.
    const body = text.slice(leading.length);
    if (body.startsWith('#')) continue;

    const prev = findPreviousNonEmptyLine(lines, i);
    if (prev && isBlockOpener(prev.text)) continue;

    diagnostics.push({
      from,
      to: from + leading.length,
      severity: 'error',
      source: 'indent',
      message:
        'Unerwartete Einrückung: Top-Level-Zeilen beginnen in Python bei Spalte 0.',
    });

    // One diagnostic per offending line is enough; highlight the leading
    // whitespace run so the user sees where the indent starts.
    void to;
  }

  return diagnostics;
}

function findPreviousNonEmptyLine(
  lines: { text: string }[],
  index: number
): { text: string } | undefined {
  for (let j = index - 1; j >= 0; j--) {
    if (lines[j].text.trim().length > 0) return lines[j];
  }
  return undefined;
}

function isBlockOpener(text: string): boolean {
  // Strip trailing comment + whitespace, then check for `:` terminator.
  // The scan is conservative: a `:` inside a string literal on the same
  // line would trigger a false negative (we'd reject a legitimate
  // block). That is acceptable — the diagnostic reads as a warning and
  // the user can dismiss it by fixing the indent deliberately.
  const stripped = text.replace(/#.*$/, '').trimEnd();
  return stripped.endsWith(':');
}

export function collectSyntaxDiagnostics(
  state: EditorState,
  language: SyntaxLinterLanguage
): Diagnostic[] {
  const diagnostics = collectLezerErrors(state);
  if (language === 'python') {
    diagnostics.push(...checkPythonTopLevelIndent(state));
  }
  return diagnostics;
}

export function syntaxLinterExtension(language: SyntaxLinterLanguage) {
  return linter(view => collectSyntaxDiagnostics(view.state, language));
}
